/**
 * Pure helper utilities for the NewReadiness view.
 * Extracted here so they can be unit-tested independently.
 */

/**
 * Remove "no_filter" entries from saved filters whenever "requires_recalc" is
 * present, so the two filters are never applied together.
 *
 * @param {string[]|null} filters
 * @returns {string[]}
 */
export const getCleanedFilters = (filters) => {
  if (!filters) return [];
  if (filters.includes("requires_recalc")) {
    return filters.filter((item) => item !== "no_filter");
  }
  return filters;
};

/**
 * Resolve the last-month readiness value from an account-details payload,
 * preferring the employee-averaged field over the account-level field.
 *
 * @param {{ last_month_avg_employee_readiness?: number|null, last_month_readiness?: number|null }} d
 * @returns {number|null}
 */
export const resolveLastMonthReadiness = (d) => {
  if (
    d.last_month_avg_employee_readiness !== null &&
    d.last_month_avg_employee_readiness !== undefined
  ) {
    return Number(d.last_month_avg_employee_readiness);
  }
  if (d.last_month_readiness !== null && d.last_month_readiness !== undefined) {
    return Number(d.last_month_readiness);
  }
  return null;
};

/**
 * Apply a new importance value to the employee's local state immediately so
 * the UI reflects the change before the API round-trip completes.
 *
 * @param {Object} employee
 * @param {'primary'|'preparing_for'} kind
 * @param {number} newValue
 */
export const applyLocalImportanceState = (employee, kind, newValue) => {
  employee.needs_recalc = true;
  if (kind === "primary") {
    employee.primary_importance = newValue;
    if (employee.primary_position) {
      employee.primary_position.importance = newValue;
    } else if (employee.primary_positions?.[0]) {
      employee.primary_positions[0].importance = newValue;
    }
  } else if (kind === "preparing_for") {
    employee.preparing_for_importance = newValue;
    if (employee.preparing_for_position) {
      employee.preparing_for_position.importance = newValue;
    } else if (employee.preparing_for_positions?.[0]) {
      employee.preparing_for_positions[0].importance = newValue;
    }
  }
};

/**
 * Return the position-assignment ID for a given assignment kind from the
 * employee object, or null when no matching assignment is found.
 *
 * @param {Object} employee
 * @param {'primary'|'preparing_for'|string} kind
 * @returns {number|null}
 */
export const resolveAssignmentId = (employee, kind) => {
  if (kind === "primary") {
    return employee.primary_position?.id || employee.primary_positions?.[0]?.id || null;
  }
  if (kind === "preparing_for") {
    return employee.preparing_for_position?.id || employee.preparing_for_positions?.[0]?.id || null;
  }
  return null;
};

/**
 * Derive the averageReadiness and lastMonthReadiness values from an
 * account-details API payload. Returns plain numbers so the caller can assign
 * them to reactive refs without mixing pure logic with Vue internals.
 *
 * @param {Object|null} d - account-details payload
 * @returns {{ averageReadiness: number, lastMonthReadiness: number|null }}
 */
export const computeAccountReadiness = (d) => {
  if (!d) return { averageReadiness: 0, lastMonthReadiness: null };

  // Employees Readiness banner must follow avg_employee_readiness (legacy behavior).
  // current_readiness is an account-level field and can stay stale after
  // importance changes to medium/low while avg_employee_readiness updates.
  let averageReadiness = 0;
  if (d.avg_employee_readiness !== null && d.avg_employee_readiness !== undefined) {
    averageReadiness = Number(d.avg_employee_readiness);
  } else if (d.current_readiness !== null && d.current_readiness !== undefined) {
    averageReadiness = Number(d.current_readiness);
  }

  return {
    averageReadiness,
    lastMonthReadiness: resolveLastMonthReadiness(d),
  };
};

/**
 * Compute a fallback average readiness from a list of employees when the
 * backend account endpoint returns 0 or null. Employees flagged with
 * exclude_from_avg are excluded from the calculation.
 *
 * @param {Object[]} employeeList
 * @returns {number|undefined} rounded average, or undefined when no eligible employees exist
 */
export const computeFallbackAverageReadiness = (employeeList) => {
  const included = (employeeList || []).filter((emp) => !emp.exclude_from_avg);
  if (included.length === 0) return undefined;
  const sum = included.reduce((acc, emp) => acc + (Number(emp.current_readiness) || 0), 0);
  return Math.round(sum / included.length);
};

/**
 * After an importance-driven recalc, prefer the account API average but fall
 * back to a page-scoped employee average when the API aggregate is unchanged
 * (backend often lags updating avg_employee_readiness).
 *
 * @param {Object|null} accountData
 * @param {Object[]} employeeList
 * @param {number} baselineAvg - banner value before the importance change
 * @returns {{ averageReadiness: number, lastMonthReadiness: number|null }}
 */
export const resolveAverageReadinessAfterRecalc = (accountData, employeeList, baselineAvg) => {
  const fromApi = computeAccountReadiness(accountData);
  if (fromApi.averageReadiness !== baselineAvg) {
    return fromApi;
  }
  const fromEmployees = computeFallbackAverageReadiness(employeeList);
  if (fromEmployees !== undefined && fromEmployees !== fromApi.averageReadiness) {
    return { ...fromApi, averageReadiness: fromEmployees };
  }
  return fromApi;
};

/**
 * Returns true when the account-details recalc polling loop should stop.
 *
 * @param {Object} params
 * @returns {boolean}
 */
export const shouldExitRecalcPoll = ({
  baselineAvg,
  currentAvg,
  elapsedMs,
  sawRecalcInProgress,
  readinessRecalcInProgress,
  hasAccountData = false,
  minWaitMs = 1500,
  settleWaitMs = 3000,
  minAvgChangeWaitMs = 500,
}) => {
  if (baselineAvg !== undefined && currentAvg !== baselineAvg && elapsedMs >= minAvgChangeWaitMs) {
    return true;
  }
  if (
    sawRecalcInProgress &&
    hasAccountData &&
    !readinessRecalcInProgress &&
    elapsedMs >= minWaitMs
  ) {
    return true;
  }
  if (elapsedMs >= settleWaitMs) {
    return true;
  }
  return false;
};

/**
 * Patch an employee row with fields returned by recalculate-readiness.
 *
 * @param {Object|null} employee
 * @param {{ current_readiness?: number, needs_recalc?: boolean }|null} data
 */
export const applyRecalcResponseToEmployee = (employee, data) => {
  const normalized = data?.employee ? { ...data.employee } : data;
  if (!employee || !normalized) return;
  if (normalized.current_readiness != null) {
    employee.current_readiness = normalized.current_readiness;
  }
  if (normalized.needs_recalc != null) {
    employee.needs_recalc = normalized.needs_recalc;
  }
  if (normalized.readiness_status != null) {
    employee.readiness_status = normalized.readiness_status;
  }
};
